import { HydraMember } from '../../../../types/api'
import { CompanyEntity } from '../../../../types/entities'
import { ReferralIndividual, ReferralTypeOption } from '../../../../types/misc'
import { CompanyServiceItem } from '../../../../types/responses/companies'

export interface OutlookReferralFormValues {
  fullName: string
  email: string
  circumstances?: string
  telephone?: string
  dateOfBirth?: string
  addressLine1?: string
  city?: string
  postcode?: string
  referralIndividuals: ReferralIndividual[]
  company?: OutlookReferralCompany
  adviserEmail: string
  services: CompanyServiceItem[]
  hasCommercialAgreement?: boolean
  reason?: string
  isExpectingCommission?: boolean
  expectedCommissionAmount?: number
  knowsCommissionAmount?: boolean
  referralType?: ReferralTypeOption
  confirmIncidentalReferral?: boolean
  hasConfirmedOutgoingAgreement?: boolean

  // The below fields are not real form fields but are only used to store
  // validation errors. Maybe there's a better way to do this
  hasAddedOutgoingAgreementTerms?: string
  existingAdviserTarget?: string
}

export const OUTLOOK_REFERRAL_FORM_UNMAPPED_ERROR = {
  existingAdviserTarget: 'existingAdviserTarget',
}

export type OutlookReferralCompany = HydraMember<
  Pick<CompanyEntity, 'id' | 'presentationName' | 'companyTypeCode'>
>

export const OutlookReferralFormFields: Record<
  keyof OutlookReferralFormValues,
  keyof OutlookReferralFormValues
> = {
  fullName: 'fullName',
  email: 'email',
  circumstances: 'circumstances',
  telephone: 'telephone',
  dateOfBirth: 'dateOfBirth',
  addressLine1: 'addressLine1',
  city: 'city',
  postcode: 'postcode',
  referralIndividuals: 'referralIndividuals',
  company: 'company',
  adviserEmail: 'adviserEmail',
  services: 'services',
  hasCommercialAgreement: 'hasCommercialAgreement',
  reason: 'reason',
  isExpectingCommission: 'isExpectingCommission',
  expectedCommissionAmount: 'expectedCommissionAmount',
  knowsCommissionAmount: 'knowsCommissionAmount',
  referralType: 'referralType',
  confirmIncidentalReferral: 'confirmIncidentalReferral',
  hasConfirmedOutgoingAgreement: 'hasConfirmedOutgoingAgreement',
  hasAddedOutgoingAgreementTerms: 'hasAddedOutgoingAgreementTerms',
  existingAdviserTarget: 'existingAdviserTarget',
}

export const OutlookReferralFormOptionalClientFields: string[] = [
  OutlookReferralFormFields.dateOfBirth,
  OutlookReferralFormFields.circumstances,
  OutlookReferralFormFields.addressLine1,
  OutlookReferralFormFields.city,
  OutlookReferralFormFields.postcode,
]

export const OUTLOOK_REFERRAL_FORM_INITIAL_VALUES: OutlookReferralFormValues = {
  fullName: '',
  email: '',
  adviserEmail: '',
  circumstances: '',
  telephone: '',
  dateOfBirth: '',
  referralIndividuals: [],
  services: [],
  addressLine1: '',
  city: '',
  postcode: '',
}

// If making changes to this or interceptor logic make sure to apply in the API as well
export const REFERRAL_BODY_PHRASES: string[] = [
  'refer * to you',
  'referring * to you',
  'would like to refer',
  'wish to refer',
  'introduce * to you',
  'introduction * between',
  'introduction between',
  'introduction to',
  'introduced * to',
  'connect * with you',
  'connect you with',
  'connect with * regarding',
  'recommend * speak with you',
  'recommended * engage',
  'believe * would benefit from',
  'pass * to you',
  'hand * over to you',
  'share your contact details with',
  'shared your contact details',
  'shared your details with',
  'assist * with',
  'assistance * with',
  'arrange a meeting with',
  'arrange an introduction to',
  'seek your advice on',
  'seek advice from',
  'consult with * regarding',
  'consultation with',
  'cc’d * from',
  'cc’d * of',
  'cc’ing * with',
  'adding * from * to this email thread',
  'ccing * from',
  'connecting you with',
  'consider reaching out to',
  'contact * at',
  'get in touch with',
  'give * a call',
  'i want to connect you with',
  'i’ll let * take it from here',
  'i’m happy to introduce you to',
  'introduce * from',
  'introduce you to',
  'introduce you two via',
  'introducing you two via',
  'it might be a good idea to contact *',
  'let me connect you with',
  'looping in * from',
  'many of our clients have had great results with',
  'may i suggest * from',
  'please contact * at * for assistance',
  'please meet',
  'put you in touch with',
  'putting you in touch with',
  'reach out to',
  'reach out to * directly to discuss',
  'should you need a * i know * who can help',
  'speak with * from',
  'to introduce',
  'we often refer our clients to',
  'you should contact',
  'onward referral to',
  'forward * to you',
  'forward this to',
  'share * details with',
  'transfer * to you',
  'please liaise with',
  'escalate * to you',
  'delegate * to',
  'please follow up with',
  'put * in contact with',
  'seek * assistance from',
]
